import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import './AIQA.css';

// API 配置

const API_CHAT_ENDPOINT = `/aiProxy/v1/chat/completions`;

const AIQA = () => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'ai',
      text: '您好，我是星晟 AI 工程顾问。我可以为您解答工程法规、费用标准、项目管理、成本控制等方面的专业问题。'
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);

  // 自动滚动到底部
  const scrollToBottom = () => {
    // messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const heroStats = [
    { label: '覆盖法规与标准', value: '2,000+' },
    { label: '平均响应时间', value: '1.2 秒' },
    { label: '行业知识库', value: '30+ 赛道' },
    { label: '在线可用', value: '7×24h' }
  ];

  const featureHighlights = [
    { title: '法规实时更新', desc: '自动同步住建部、发改委等权威政策，确保咨询内容符合最新标准。' },
    { title: '专家答案校验', desc: 'AI 回答结合行业专家模板，提供更可执行的建议与路径。' },
    { title: '可追踪知识库', desc: '所有提问自动归档，形成企业内部知识库，支持检索与复用。' }
  ];

  const exampleQuestions = [
    '最新版工程建设监理费收费标准是什么？',
    '建筑工程安全生产管理条例有哪些关键要求？',
    '如何准确计算项目建设管理费？',
    '工程合同履约风险如何有效控制？'
  ];

  const handleSend = async () => {
    if (inputValue.trim() === '' || isLoading) return;

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: inputValue.trim(),
      model: "Qwen2.5-VL-7B-Instruct",
      messages: [
        {
          "role": "user",
          "content": inputValue.trim()
        }
      ],
      temperature: 0.7
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    // 添加加载中的消息
    const loadingMessageId = Date.now() + 1;
    const loadingMessage = {
      id: loadingMessageId,
      sender: 'ai',
      text: '正在思考中...',
      isLoading: true
    };
    setMessages((prev) => [...prev, loadingMessage]);
    try {
      const response = await fetch(API_CHAT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          userMessage,
          // 可选：发送历史消息上下文
          // history: messages
          //   .filter(msg => msg.sender !== 'ai' || !msg.isLoading)
          //   .slice(-10) // 只发送最近10条消息作为上下文
          //   .map(msg => ({
          //     role: msg.sender === 'user' ? 'user' : 'assistant',
          //     content: msg.text
          //   }))
        ),
      });

      if (!response.ok) {
        throw new Error(`请求失败: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // 移除加载中的消息
      setMessages((prev) => prev.filter(msg => msg.id !== loadingMessageId));

      // 处理不同的响应格式
      let aiText = '';
      console.log(data);

      if (data.choices && data.choices[0].message.content) {
        // API 返回格式: {"success": true, "response": "..."}
        aiText = data.choices[0].message.content;
      } else {
        aiText = '抱歉，我暂时无法回答这个问题。';
      }

      // 添加 AI 响应
      const aiResponse = {
        id: Date.now(),
        sender: 'ai',
        text: aiText,
        isLoading: false
      };
      setMessages((prev) => [...prev, aiResponse]);
    } catch (err) {
      console.error('API 请求错误:', err);

      // 移除加载中的消息
      setMessages((prev) => prev.filter(msg => msg.id !== loadingMessageId));

      // 根据错误类型提供不同的错误消息
      let errorText = '抱歉，服务暂时不可用。请稍后再试，或联系我们的技术支持。';
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        errorText = '网络连接失败，请检查网络连接或确认 API 服务是否正常运行。';
      } else if (err.message.includes('CORS')) {
        errorText = '跨域请求被阻止，请联系技术支持配置 CORS 设置。';
      }

      // 添加错误消息
      const errorMessage = {
        id: Date.now(),
        sender: 'ai',
        text: errorText,
        isError: true
      };
      setMessages((prev) => [...prev, errorMessage]);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (question) => {
    setInputValue(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <div className="ai-qa">
      <motion.section
        className="hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.p
            className="eyebrow"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            AI CONSULTANT
          </motion.p>
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            星晟 AI 工程顾问
          </motion.h1>
          <motion.p
            className="intro"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            基于星晟行业知识图谱与大语言模型，7×24 小时在线回答工程法规、费用标准、项目管理等专业问题，成为工程团队的即时智囊团。
          </motion.p>
          <motion.div
            className="hero-stats"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {heroStats.map((stat, index) => (
              <motion.div
                className="stat-card"
                key={stat.label}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
              >
                <span className="value" style={{ color: '#fff' }}>{stat.value}</span>
                <span className="label">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      <motion.section
        className="ai-highlights"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="section-header">
            <motion.p
              className="eyebrow"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              WHY AI CONSULTANT
            </motion.p>
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              随问随答，让工程问题“不等人”
            </motion.h2>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              结合行业专家库与AI引擎，为您提供可信、可落地的工程咨询建议。
            </motion.p>
          </div>
          <div className="highlight-grid">
            {featureHighlights.map((item, index) => (
              <motion.div
                className="highlight-card"
                key={item.title}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        className={`chat-interface ${isFullscreen ? 'fullscreen' : ''}`}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        {isFullscreen && (
          <div className="fullscreen-overlay" onClick={() => setIsFullscreen(false)}></div>
        )}
        <div className={`container ${isFullscreen ? 'fullscreen-container' : ''}`}>
          <div className="chat-content">
            <div className={`chat-panel ${isFullscreen ? 'fullscreen-panel' : ''}`}>
              {isFullscreen && (
                <div className="fullscreen-header">
                  <h3>星晟 AI 工程顾问</h3>
                  <button
                    className="close-fullscreen-button"
                    onClick={() => setIsFullscreen(false)}
                    aria-label="关闭全屏"
                  >
                    ✕
                  </button>
                </div>
              )}
              {!isFullscreen && (
                <div className="chat-header">
                  <h3>开始对话</h3>
                  <button
                    className="fullscreen-button"
                    onClick={() => setIsFullscreen(true)}
                    aria-label="全屏"
                  >
                    ⛶
                  </button>
                </div>
              )}
              <motion.div
                className={`chat-messages ${isFullscreen ? 'fullscreen-messages' : ''}`}
                ref={chatMessagesRef}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    className={`message ${message.sender} ${message.isError ? 'error' : ''}`}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="message-content">
                      {message.isLoading ? (
                        <div className="loading-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      ) : (
                        <div style={{ whiteSpace: 'pre-line' }}>{message.text}</div>
                      )}
                    </div>
                  </motion.div>
                ))}
                <div ref={messagesEndRef} />
              </motion.div>

              <motion.div
                className="chat-input"
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="input-container">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="在此输入您的问题，例如：二级建造师的报考条件？"
                  />
                  <button
                    className="send-button"
                    onClick={handleSend}
                    disabled={isLoading || inputValue.trim() === ''}
                  >
                    {isLoading ? '发送中...' : '发送'}
                  </button>
                </div>
                <div className="example-questions">
                  <p>试试提问：</p>
                  <div className="questions">
                    {exampleQuestions.map((question, index) => (
                      <motion.button
                        key={index}
                        className="example-button"
                        onClick={() => handleExampleClick(question)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                      >
                        {question}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
            {!isFullscreen && (
              <motion.div
                className="chat-side-panel"
                initial={{ x: 30, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h3 style={{ color: '#fff' }}>安全提示</h3>
                <p>AI 回答由大语言模型生成，可能存在疏漏，仅供参考，不构成专业法律或财务建议。</p>
                <ul>
                  <li>可导出对话记录，便于归档。</li>
                  <li>支持接入企业知识库，回答更贴近内部流程。</li>
                  <li>如需权威解释，可一键连接真人顾问。</li>
                </ul>
              </motion.div>
            )}
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default AIQA;